import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  campaignBrandsService,
  AdminCampaign,
  CampaignSearchParams,
  SearchAdminCampaignsResponse,
} from "../../services/admin/campaigns.admin.service";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { AdminFilters } from "../../components/admin/AdminFilters";
import {
  RefreshCw,
  Calendar,
  DollarSign,
  Users,
  TrendingUp,
  Award,
  Activity,
  ExternalLink,
  Tag,
} from "lucide-react";
import { format } from "date-fns";
import { AdminCampaignDetailModal } from "@/components/admin/AdminCampaignDetailModal";

// Skeleton Components
function StatCardSkeleton() {
  return (
    <div className="bg-transparent border border-border rounded-xl animate-pulse p-6">
      <div className="flex items-center justify-between space-x-4">
        <div className="space-y-2 flex-1">
          <div className="h-3 bg-muted rounded w-1/2"></div>
          <div className="h-8 bg-muted rounded w-1/3"></div>
        </div>
        <div className="h-12 w-12 rounded-2xl border border-border bg-transparent"></div>
      </div>
    </div>
  );
}

// Stats Card Component
interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  formatter?: (value: number) => string;
}

function StatCard({ title, value, icon, color, formatter }: StatCardProps) {
  const displayValue =
    typeof value === "number" && formatter ? formatter(value) : value;
  const isWarm = color === "bg-orange-100 dark:bg-orange-500/15";

  return (
    <div className="bg-transparent border border-border rounded-xl transition-all duration-300 hover:border-[#d7785b]/40">
      <div className="p-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2 flex-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {title}
            </p>
            <p className="text-3xl font-bold text-foreground">{displayValue}</p>
          </div>
          <div
            className={`h-12 w-12 rounded-2xl flex items-center justify-center border ${
              isWarm
                ? "border-[#d7785b]/20 bg-transparent"
                : "border-border bg-transparent"
            }`}
          >
            <div className={isWarm ? "text-[#d7785b]" : "text-muted-foreground"}>
              {icon}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminCampaigns() {
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] =
    useState<CampaignSearchParams["sortOrder"]>();
  const [selectedCampaign, setSelectedCampaign] =
    useState<AdminCampaign | null>(null);
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]);
  const [brandId, setBrandId] = useState<string>();
  const [showModal, setShowModal] = useState(false);
  const [status, setStatus] = useState<CampaignSearchParams["status"]>();
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filterBrands, setFilterBrands] = useState<
    { id: string; name: string }[]
  >([]);

  // Refs to track current search parameters without causing re-renders
  const searchParamsRef = useRef<CampaignSearchParams>({
    page: 1,
    limit: 10,
  });

  // Update ref whenever state changes
  useEffect(() => {
    searchParamsRef.current = {
      page,
      limit: pageSize,
      ...(status && { status }),
      ...(searchQuery && { keyword: searchQuery }),
      ...(brandId && { brandId }),
      ...(sortOrder && { sortOrder }),
    };
  }, [page, pageSize, searchQuery, brandId, status, sortOrder]);

  useEffect(() => {
    const fetchFilterBrands = async () => {
      const brands = await campaignBrandsService.getFilteringBrands();
      setFilterBrands(brands);
    };
    fetchFilterBrands();
  }, []);

  // Stats
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalSales: 0,
    averageSalesPerCampaign: 0,
  });

  // Stable search function that doesn't depend on state
  const performSearch = useCallback(async (params: CampaignSearchParams) => {
    setLoading(true);
    try {
      const response: SearchAdminCampaignsResponse =
        await campaignBrandsService.searchCampaigns(params);
      setCampaigns(response.data);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Failed to search campaigns:", error);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search with current parameters
  const searchWithCurrentParams = useCallback(() => {
    performSearch(searchParamsRef.current);
  }, [performSearch]);

  // Debounced search
  const debounceRef = useRef<NodeJS.Timeout>();
  const debouncedSearch = useCallback(
    (overrideParams?: Partial<CampaignSearchParams>) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        const params = {
          ...searchParamsRef.current,
          ...overrideParams,
        };
        performSearch(params);
      }, 1000);
    },
    [performSearch]
  );

  // Filter handlers - only update state, don't trigger search immediately
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPage(1);
    debouncedSearch({ keyword: value, page: 1 });
  };

  const handleStatusChange = (value: string | string[]) => {
    const newStatus = Array.isArray(value) ? value[0] : value;
    setStatus(newStatus as CampaignSearchParams["status"]);
    setPage(1);
    debouncedSearch({
      status: newStatus as CampaignSearchParams["status"],
      page: 1,
    });
  };

  const handleBrandChange = (value: string | string[]) => {
    const newBrand = Array.isArray(value) ? value[0] : value;
    setBrandId(newBrand as string);
    setPage(1);
    debouncedSearch({ brandId: newBrand as string, page: 1 });
  };

  const handleSortOrderChange = (value: string | string[]) => {
    const newSortOrder = Array.isArray(value) ? value[0] : value;
    setSortOrder(newSortOrder as CampaignSearchParams["sortOrder"]);
    setPage(1);
    debouncedSearch({
      sortOrder: newSortOrder as CampaignSearchParams["sortOrder"],
      page: 1,
    });
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSortOrder(undefined);
    setPage(1);
    setStatus(undefined);
    setBrandId("");

    // Clear any pending debounced search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Perform immediate search with reset params
    performSearch({ page: 1, limit: pageSize });
  };

  const handleApplyFilters = () => {
    setPage(1);
    // Clear any pending debounced search and search immediately
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    performSearch({ ...searchParamsRef.current, page: 1 });
  };

  // Fetch stats
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const statsData = await campaignBrandsService.getCampaignStats();
      setStats(statsData);
    } catch (error) {
      console.error("Failed to fetch brand stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Handle pagination change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // Immediate search for pagination, no debounce needed
    performSearch({ ...searchParamsRef.current, page: newPage });
  };

  // Initial load
  useEffect(() => {
    searchWithCurrentParams();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle view brand details
  const handleViewCampaign = async (campaign: AdminCampaign) => {
    setSelectedCampaign(campaign);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCampaign(null);
  };

  // Format currency
  const formatCurrency = (value: number) =>
    `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return " text-green-800 dark:text-green-300";
      case "PAUSED":
        return "text-yellow-800 dark:text-yellow-300";
      case "INACTIVE":
        return "text-red-800 dark:text-red-300";
      default:
        return "text-foreground";
    }
  };

  // Table columns
  const columns: ColumnDef<AdminCampaign>[] = [
    {
      id: "title",
      header: "Campaign Title",
      meta: { width: "w-52" },
      cell: ({ row }) => (
        <div className="font-medium text-foreground truncate">
          {row.original.title}
        </div>
      ),
    },
    {
      id: "link",
      header: "Campaign Link",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <ExternalLink className="w-3 h-3 text-muted-foreground" />
          <a
            href={row.original.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800"
          >
            View Link
          </a>
        </div>
      ),
    },
    {
      id: "status",
      header: "Campaign Status",
      meta: { width: "w-36" },
      cell: ({ row }) => (
        <div
          className={`flex items-center gap-1 text-sm text-foreground ${getStatusBadgeColor(
            row.original.status
          )}`}
        >
          {row.original.status}
        </div>
      ),
    },
    {
      id: "code",
      header: "Campaign Code",
      meta: { width: "w-36" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Tag className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="truncate">{row.original.code}</span>
        </div>
      ),
    },
    {
      id: "targetAllProducts",
      header: "Target Products",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Tag className="w-3 h-3 text-muted-foreground" />
          {row.original.targetAllProducts}
        </div>
      ),
    },
    {
      id: "createdAt",
      header: "Created",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Calendar className="w-3 h-3 text-muted-foreground" />
          {format(new Date(row.original.createdAt), "MMM dd, yyyy")}
        </div>
      ),
    },
    {
      id: "discountValue",
      header: "Discount Value",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <span className="text-sm text-muted-foreground">
            {formatCurrency(row.original.discountValue)}
          </span>
        </div>
      ),
    },
    {
      id: "totalSales",
      header: "Total Sales",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <DollarSign className="w-3 h-3 text-muted-foreground" />
          <span
            className={`font-medium ${
              row.original.totalSales > 0
                ? "text-green-600 dark:text-green-400"
                : "text-muted-foreground"
            }`}
          >
            {formatCurrency(row.original.totalSales)}
          </span>
        </div>
      ),
    },
    {
      id: "salesCount",
      header: "Sales Count",
      meta: { width: "w-28" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Users className="w-3 h-3 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {row.original.salesCount}
          </span>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-semibold text-foreground mb-2">
            Campaign Management
          </h1>
          <p className="text-muted-foreground">
            Manage and monitor all campaigns on your platform.
          </p>
        </div>
        <button
          onClick={() => {
            searchWithCurrentParams();
            fetchStats();
          }}
          className="flex items-center gap-2 px-6 py-3 text-white rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
          style={{ backgroundColor: "#d7785b" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#c66a50";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#d7785b";
          }}
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-8">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <StatCardSkeleton key={index} />
          ))
        ) : (
          <>
            <StatCard
              title="Total Campaigns"
              value={stats.totalCampaigns}
              icon={<Tag className="w-5 h-5 text-foreground" />}
              color="bg-muted"
            />
            <StatCard
              title="Active Campaigns"
              value={stats.activeCampaigns}
              icon={
                <Activity className="w-5 h-5" style={{ color: "#d7785b" }} />
              }
              color="bg-orange-100 dark:bg-orange-500/15"
            />
            <StatCard
              title="Total Sales"
              value={stats.totalSales}
              icon={<TrendingUp className="w-5 h-5 text-foreground" />}
              color="bg-muted"
              formatter={formatCurrency}
            />
            <StatCard
              title="Avg. Sales"
              value={stats.averageSalesPerCampaign}
              icon={<Award className="w-5 h-5" style={{ color: "#d7785b" }} />}
              color="bg-orange-100 dark:bg-orange-500/15"
              formatter={formatCurrency}
            />
          </>
        )}
      </div>

      {/* Enhanced Filters */}
      <div className="">
        <AdminFilters
          filters={[
            {
              label: "Sort Order",
              value: sortOrder || "",
              onChange: handleSortOrderChange,
              showLabel: true,
              options: [
                { label: "Descending", value: "DESC" },
                { label: "Ascending", value: "ASC" },
              ],
            },
            {
              label: "Status",
              value: status || "",
              onChange: handleStatusChange,
              showLabel: true,
              options: [
                { label: "Active", value: "ACTIVE" },
                { label: "Paused", value: "PAUSED" },
              ],
            },

            {
              label: "Brand",
              value: brandId || "",
              onChange: handleBrandChange,
              options: filterBrands.map((brand) => ({
                label: brand.name,
                value: brand.id,
              })),
            },
          ]}
          searchPlaceholder="Search campaigns by title..."
          onSearch={handleSearch}
          searchValue={searchQuery}
          onReset={handleResetFilters}
          onApply={handleApplyFilters}
        />
      </div>

      {/* Campaigns Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm">
        <div className="p-8 border-b border-border">
          <h3 className="text-xl font-semibold text-foreground">
            {loading
              ? "Loading Campaigns..."
              : `Campaigns (${total.toLocaleString()})`}
          </h3>
        </div>

        <DataTable
          data={campaigns}
          columns={columns}
          isLoading={loading}
          emptyText={
            searchQuery || status || brandId
              ? "No campaigns found matching your search criteria"
              : "No campaigns have been created yet"
          }
          onRowClick={handleViewCampaign}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
              performSearch({
                ...searchParamsRef.current,
                limit: size,
                page: 1,
              });
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            onPageChange={handlePageChange}
            noun="campaigns"
          />
        )}
      </div>
      {showModal && selectedCampaign && (
        <AdminCampaignDetailModal
          isOpen={showModal}
          onClose={closeModal}
          campaign={selectedCampaign}
        />
      )}
    </div>
  );
}
